"use client";

import {
  Button,
  FormNumber,
  FormPrice,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  RHF,
  toast,
  useDebounceCallback,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  PlusIcon,
  ScanBarcodeIcon,
  Trash2Icon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Resolver } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { CreatePurchaseOrderSchema } from "@/modules/merchant/domain/contracts";
import type {
  ShopBranchOption,
  ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import {
  shopCatalogApi,
  shopInventoryApi,
} from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { DatePickerField } from "@/modules/merchant/presentation/ui/DatePickerField";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { SHOP_CURRENCIES, shopCurrencySymbol } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  useCreatePurchaseOrderMutation,
  useLookupSellableByBarcodeMutation,
  useSuppliersListQuery,
} from "../api/queries";

const LIST_ROUTE = "/app/shops/$shopId/purchase-orders" as const;
const DETAIL_ROUTE =
  "/app/shops/$shopId/purchase-orders/$purchaseOrderId" as const;

/** ຄືກັນກັບ POS / ໂອນສະຕ໋ອກ — ບໍ່ກັ່ນຮູບແບບ barcode */
const PO_SCAN_MAX_LEN = 100;

const PoFormSchema = CreatePurchaseOrderSchema.extend({
  lines: z.array(
    z.object({
      shopProductSkuId: z.string(),
      qtyOrdered: z.coerce.number().int().min(1),
      unitCost: z.union([z.coerce.number().nonnegative(), z.null()]).optional(),
      lineNote: z.string().max(2000).optional(),
    }),
  ),
}).superRefine((data, ctx) => {
  for (let i = 0; i < data.lines.length; i++) {
    const id = data.lines[i]?.shopProductSkuId?.trim() ?? "";
    if (!z.string().uuid().safeParse(id).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກ SKU ທຸກບັນທັດ",
        path: ["lines", i, "shopProductSkuId"],
      });
    }
  }
});

type PoFormValues = z.infer<typeof PoFormSchema>;

export function ShopPurchaseOrderNewPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: LIST_ROUTE });
  const { currentShop } = useMerchantShops(shopId);
  const createPo = useCreatePurchaseOrderMutation(shopId);
  const suppliersQuery = useSuppliersListQuery(shopId, {
    limit: 100,
    offset: 0,
    activeOnly: true,
  });
  const lookup = useLookupSellableByBarcodeMutation(shopId);

  const barcodeFieldId = useId();
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const scanLockRef = useRef(false);
  const [barcode, setBarcode] = useState("");

  const defaultCurrency = currentShop?.defaultCurrency ?? "LAK";

  // ຄ່າເລີ່ມຕົ້ນ = ວັນທີມື້ນີ້ (local, yyyy-mm-dd) ໃຫ້ກ່ອງ "ວັນທີສັ່ງ" ສະແດງເລີຍ
  const todayLocal = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();

  const form = RHF.useForm<PoFormValues>({
    resolver: zodResolver(PoFormSchema) as Resolver<PoFormValues>,
    defaultValues: {
      branchId: "",
      currency: defaultCurrency,
      supplierId: undefined,
      supplierName: undefined,
      supplierRef: undefined,
      orderedAt: todayLocal,
      expectedAt: undefined,
      note: undefined,
      lines: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "lines",
  });

  const addLineFromPoScan = useCallback(
    (row: ShopSellableCatalogRow) => {
      const cur = form.getValues("currency");
      if (row.currency !== cur) {
        toast.error("ສະກຸນເງິນສິນຄ້າບໍ່ກົງກັບຫົວ PO");
        return;
      }
      const lines = form.getValues("lines");
      const i = lines.findIndex((l) => l.shopProductSkuId === row.id);
      if (i >= 0) {
        const nextQty = (lines[i]?.qtyOrdered ?? 0) + 1;
        form.setValue(`lines.${i}.qtyOrdered`, nextQty, {
          shouldValidate: true,
        });
      } else {
        append({
          shopProductSkuId: row.id,
          qtyOrdered: 1,
          unitCost: null,
          lineNote: undefined,
        });
      }
    },
    [append, form],
  );

  const runScanForPo = useCallback(
    async (raw: string) => {
      const b = raw.trim();
      if (!b || !shopId) return;
      if (b.length > PO_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${PO_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      if (scanLockRef.current) return;
      scanLockRef.current = true;
      try {
        const result = await lookup.mutateAsync(b);
        if (result.meta.total === 0) {
          toast.error("ບໍ່ພົບສິນຄ້າດ້ວຍ barcode ນີ້");
          return;
        }
        if (result.meta.total > 1) {
          toast.warning(`ພົບ ${result.meta.total} ລາຍການ — ໃຊ້ລາຍການທຳອິດ`);
        }
        const first = result.data[0];
        if (first) addLineFromPoScan(first);
        setBarcode("");
        queueMicrotask(() => barcodeInputRef.current?.focus());
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາລົ້ມເຫຼວ");
      } finally {
        scanLockRef.current = false;
      }
    },
    [shopId, lookup, addLineFromPoScan],
  );

  const runScanRef = useRef(runScanForPo);
  runScanRef.current = runScanForPo;

  const debouncedAutoScan = useDebounceCallback((value: string) => {
    const live = barcodeInputRef.current?.value ?? "";
    if (live !== value) return;
    void runScanRef.current(value);
  }, 90);

  const resolveBarcodeForLine = useCallback(
    async (lineIndex: number, raw: string) => {
      const b = raw.trim();
      if (!b || !shopId) return;
      if (b.length > PO_SCAN_MAX_LEN) {
        toast.error(`ລະຫັດຍາວເກີນໄປ (ສູງສຸດ ${PO_SCAN_MAX_LEN} ຕົວ)`);
        return;
      }
      try {
        const result = await lookup.mutateAsync(b);
        if (result.meta.total === 0) {
          toast.error("ບໍ່ພົບສິນຄ້າດ້ວຍ barcode ນີ້");
          return;
        }
        if (result.meta.total > 1) {
          toast.warning(`ພົບ ${result.meta.total} ລາຍການ — ໃຊ້ລາຍການທຳອິດ`);
        }
        const row = result.data[0];
        if (!row) return;
        const cur = form.getValues("currency");
        if (row.currency !== cur) {
          toast.error("ສະກຸນເງິນສິນຄ້າບໍ່ກົງກັບຫົວ PO");
          return;
        }
        const lines = form.getValues("lines");
        const dupIdx = lines.findIndex(
          (l, i) => i !== lineIndex && l.shopProductSkuId === row.id,
        );
        if (dupIdx >= 0) {
          const q = lines[lineIndex]?.qtyOrdered ?? 1;
          form.setValue(
            `lines.${dupIdx}.qtyOrdered`,
            (lines[dupIdx]?.qtyOrdered ?? 1) + q,
            { shouldValidate: true },
          );
          remove(lineIndex);
          toast.success("ລວມເຂົ້າແຖວທີ່ມີ SKU ນີ້ແລ້ວ");
          return;
        }
        form.setValue(`lines.${lineIndex}.shopProductSkuId`, row.id, {
          shouldValidate: true,
        });
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "ຄົ້ນຫາລົ້ມເຫຼວ");
      }
    },
    [shopId, lookup, form, remove],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync currency when shop loads
  useEffect(() => {
    if (currentShop?.defaultCurrency) {
      form.setValue("currency", currentShop.defaultCurrency);
    }
  }, [currentShop?.defaultCurrency, form]);

  const currencyOptions = useMemo(
    () => SHOP_CURRENCIES.map((c) => ({ value: c, label: c })),
    [],
  );

  const poCurrency = form.watch("currency") ?? defaultCurrency;

  const onSubmit = async (v: PoFormValues) => {
    if (!shopId) return;
    try {
      const res = await createPo.mutateAsync({
        branchId: v.branchId,
        currency: v.currency,
        supplierId: v.supplierId,
        supplierName: v.supplierName,
        supplierRef: v.supplierRef,
        orderedAt: v.orderedAt,
        expectedAt: v.expectedAt,
        note: v.note,
        lines: v.lines.map((l) => ({
          shopProductSkuId: l.shopProductSkuId.trim(),
          qtyOrdered: l.qtyOrdered,
          unitCost: l.unitCost ?? null,
          lineNote: l.lineNote,
        })),
      });
      toast.success("ສ້າງໃບສັ່ງຊື້ແລ້ວ");
      nav({
        to: DETAIL_ROUTE,
        params: { shopId, purchaseOrderId: res.id },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດສ້າງໄດ້");
    }
  };

  if (!shopId) return null;

  return (
    <RequireMerchantPermission
      permission={MerchantPermissions.inventory.adjust}
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.purchaseOrders(shopId)} />
      </Header>
      <Main>
        <div className="mb-6 flex items-center justify-between gap-2">
          <div>
            <h1 className="font-heading font-semibold text-xl tracking-tight">
              ສ້າງໃບສັ່ງຊື້
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ເລືອກສາຂາ ແລະສະກຸນເງິນຫົວເອກະສານ — SKU ທຸກບັນທັດຕ້ອງກົງສະກຸນນີ້
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() =>
              nav({
                to: LIST_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: 20,
                  status: undefined,
                  branchId: undefined,
                  supplierId: undefined,
                  due: undefined,
                  sortBy: undefined,
                  sortOrder: undefined,
                },
              })
            }
          >
            <ArrowLeftIcon className="size-4" aria-hidden />
            ກັບ
          </Button>
        </div>

        <FormRoot
          methods={form}
          onSubmit={(vals) => void onSubmit(vals)}
          className="mx-auto max-w-3xl space-y-6"
        >
          <RHF.Controller
            name="branchId"
            control={form.control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="space-y-2">
                <Label>ສາຂາ</Label>
                <InfiniteCombobox<ShopBranchOption>
                  queryKey={["shops", shopId, "po-new", "branch"]}
                  queryFn={async ({ search: q, pageParam }) => {
                    return shopInventoryApi.lookupBranches(shopId, {
                      page: pageParam,
                      search: q.trim() || undefined,
                      limit: 20,
                    });
                  }}
                  preloadQueryFn={(id) =>
                    shopInventoryApi.getBranch(shopId, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  value={field.value ?? ""}
                  onValueChange={(val) => {
                    field.onChange(val);
                  }}
                  placeholder="ເລືອກສາຂາ..."
                  className="h-9 w-full"
                />
              </div>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2 sm:items-start">
            <RHF.Controller
              name="currency"
              control={form.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label>ສະກຸນເງິນ (ຫົວ PO)</Label>
                  <SimpleSelect
                    value={field.value ?? defaultCurrency}
                    onValueChange={(val) =>
                      field.onChange(val as PoFormValues["currency"])
                    }
                    options={currencyOptions}
                    className="h-9 w-full"
                  />
                </div>
              )}
            />

            <RHF.Controller
              name="supplierId"
              control={form.control}
              render={({ field }) => (
                <div className="space-y-2">
                  <Label>ຜູ້ສະໜອງ (ຈາກລາຍການ)</Label>
                  <SimpleSelect
                    value={field.value ?? "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") {
                        field.onChange(undefined);
                        return;
                      }
                      field.onChange(v);
                      const row = suppliersQuery.data?.data.find(
                        (x) => x.id === v,
                      );
                      if (row) {
                        form.setValue("supplierName", row.name);
                        form.setValue("supplierRef", row.refCode ?? undefined);
                      }
                    }}
                    options={[
                      { value: "__none__", label: "ພິມເອງ / ບໍ່ມີ" },
                      ...(suppliersQuery.data?.data.map((s) => ({
                        value: s.id,
                        label: s.name,
                      })) ?? []),
                    ]}
                    className="h-9 w-full"
                  />
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    ເລືອກຈາກຕາຕະລາງຜູ້ສະໜອງ — ລະບົບເຕີມຊື່ ແລະເອກະສານອ້າງອີງໃຫ້;
                    ຫຼືປ່ອຍວ່າງແລ້ວພິມເອງ
                  </p>
                </div>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplierName">ຊື່ຜູ້ສະໜອງ (ທາງເລືອກ)</Label>
              <Input
                id="supplierName"
                {...form.register("supplierName")}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplierRef">ເອກະສານອ້າງອີງ (ທາງເລືອກ)</Label>
              <Input
                id="supplierRef"
                {...form.register("supplierRef")}
                className="h-9"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="orderedAt">ວັນທີສັ່ງ</Label>
              <RHF.Controller
                control={form.control}
                name="orderedAt"
                render={({ field }) => (
                  <DatePickerField
                    id="orderedAt"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder="ເລືອກວັນທີສັ່ງ"
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expectedAt">ຄາດວ່າຈະຮອດ</Label>
              <RHF.Controller
                control={form.control}
                name="expectedAt"
                render={({ field }) => (
                  <DatePickerField
                    id="expectedAt"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder="ເລືອກວັນທີ"
                  />
                )}
              />
            </div>
          </div>

          <FormTextarea name="note" label="ໝາຍເຫດ" rows={3} placeholder="—" />

          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <Label className="font-medium">ລາຍການສິນຄ້າ</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() =>
                  append({
                    shopProductSkuId: "",
                    qtyOrdered: 1,
                    unitCost: null,
                    lineNote: undefined,
                  })
                }
              >
                <PlusIcon className="size-3.5" aria-hidden />
                ເພີ່ມສິນຄ້າ
              </Button>
            </div>

            <div className="relative mb-4">
              <ScanBarcodeIcon
                className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-primary"
                aria-hidden
              />
              <Input
                ref={barcodeInputRef}
                id={barcodeFieldId}
                value={barcode}
                onChange={(e) => {
                  const v = e.target.value;
                  setBarcode(v);
                  debouncedAutoScan(v);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  void runScanForPo(e.currentTarget.value);
                }}
                placeholder="ລໍຖ້າສະແກນ barcode ຮ້ານ — ຫຼືພິມແລ້ວ Enter"
                className="h-10 border-border pr-3 pl-10 font-mono text-sm"
                autoComplete="off"
                disabled={lookup.isPending}
                aria-label="ສະແກນ barcode ເພື່ອເພີ່ມສິນຄ້າ"
              />
            </div>

            <div className="space-y-4">
              {fields.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  ຍັງບໍ່ມີບັນທັດ — ສະແກນ barcode ຢູ່ຊ່ອງດ້ານເທິງ ຫຼື ເພີ່ມແຖວແລ້ວເລືອກ SKU
                </p>
              ) : null}
              {fields.map((row, index) => (
                <div
                  key={row.id}
                  className="space-y-2 rounded-md border border-border/80 bg-card p-3"
                >
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[12rem] flex-1">
                      <Label className="text-xs">SKU</Label>
                      <InfiniteCombobox<ShopSellableCatalogRow>
                        queryKey={[
                          "shops",
                          shopId,
                          "po-line",
                          String(index),
                          poCurrency,
                        ]}
                        queryFn={async ({ search: q, pageParam }) => {
                          const limit = 20;
                          const page = pageParam ?? 1;
                          const offset = (page - 1) * limit;
                          const res = await shopCatalogApi.listSellable(
                            shopId,
                            {
                              offset,
                              limit,
                              q: q.trim() || undefined,
                              barcode: undefined,
                              currency: poCurrency,
                            },
                          );
                          const next =
                            offset + res.data.length < res.meta.total
                              ? page + 1
                              : null;
                          return { items: res.data, nextPage: next };
                        }}
                        preloadQueryFn={(id) =>
                          shopCatalogApi.getSellableSku(shopId, id)
                        }
                        getLabel={(item) =>
                          `${item.productName} · ${item.shopSku} (${item.currency})`
                        }
                        getValue={(item) => item.id}
                        value={
                          form.watch(`lines.${index}.shopProductSkuId`) ?? ""
                        }
                        onValueChange={(id) => {
                          form.setValue(`lines.${index}.shopProductSkuId`, id, {
                            shouldValidate: true,
                          });
                        }}
                        placeholder="ຄົ້ນຫາສິນຄ້າ..."
                        className="h-9 w-full"
                      />
                    </div>
                    <div className="min-w-[10rem] flex-1 sm:max-w-[14rem]">
                      <Label className="text-xs">Barcode</Label>
                      <div className="relative">
                        <ScanBarcodeIcon
                          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-primary"
                          aria-hidden
                        />
                        <Input
                          className="h-9 w-full pl-8 font-mono text-xs"
                          placeholder="ສະແກນ ຫຼືພິມແລ້ວ Enter"
                          autoComplete="off"
                          disabled={lookup.isPending}
                          aria-label={`Barcode ບັນທັດ ${index + 1}`}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            const el = e.currentTarget;
                            const v = el.value;
                            void resolveBarcodeForLine(index, v).then(() => {
                              el.value = "";
                            });
                          }}
                        />
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive"
                      onClick={() => remove(index)}
                      aria-label="ລົບບັນທັດ"
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormNumber
                      name={`lines.${index}.qtyOrdered`}
                      label="ຈຳນວນສັ່ງ"
                      requiredMark
                    />
                    <FormPrice
                      name={`lines.${index}.unitCost`}
                      label="ຕົ້ນທຶນຕໍ່ຫົວຫນ່ວຍ (ທາງເລືອກ)"
                      placeholder="—"
                      currencySymbol={shopCurrencySymbol(poCurrency)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={createPo.isPending}>
              {createPo.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກເປັນຮ່າງ"}
            </Button>
          </div>
        </FormRoot>
      </Main>
    </RequireMerchantPermission>
  );
}
